/**
 * Structured Logging System
 *
 * Provides consistent logging across the application with proper
 * log levels and structured data.
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogContext {
  [key: string]: unknown;
}

interface LogEntry {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: LogContext;
  error?: Error;
}

class Logger {
  private isDevelopment: boolean;
  private isProduction: boolean;

  constructor() {
    this.isDevelopment = import.meta.env.DEV;
    this.isProduction = import.meta.env.PROD;
  }

  private formatLogEntry(entry: LogEntry): void {
    const { level, message, timestamp, context, error } = entry;

    if (this.isProduction && level === 'debug') {
      return; // Skip debug logs in production
    }

    const logData = {
      level,
      message,
      timestamp,
      ...(context && { context }),
      ...(error && {
        error: {
          message: error.message,
          stack: error.stack,
          name: error.name
        }
      })
    };

    // In development, use console for better debugging
    if (this.isDevelopment) {
      const consoleMethod = level === 'error' ? console.error :
                           level === 'warn' ? console.warn :
                           console.log;

      consoleMethod(`[${level.toUpperCase()}] ${message}`, context || '');
      if (error) {
        console.error(error);
      }
      return;
    }

    // In production, use structured JSON logging
    // This can be sent to external logging services
    if (level === 'error' || level === 'warn') {
      console.error(JSON.stringify(logData));
    }
  }

  /**
   * Log debug information (development only)
   */
  debug(message: string, context?: LogContext): void {
    this.formatLogEntry({
      level: 'debug',
      message,
      timestamp: new Date().toISOString(),
      context
    });
  }

  /**
   * Log informational messages
   */
  info(message: string, context?: LogContext): void {
    this.formatLogEntry({
      level: 'info',
      message,
      timestamp: new Date().toISOString(),
      context
    });
  }

  /**
   * Log warning messages
   */
  warn(message: string, context?: LogContext): void {
    this.formatLogEntry({
      level: 'warn',
      message,
      timestamp: new Date().toISOString(),
      context
    });
  }

  /**
   * Log error messages
   */
  error(message: string, error?: Error, context?: LogContext): void {
    this.formatLogEntry({
      level: 'error',
      message,
      timestamp: new Date().toISOString(),
      context,
      error
    });
  }
}

// Export singleton instance
export const logger = new Logger();

// Helper function for logging API errors
export function logApiError(
  endpoint: string,
  error: Error,
  additionalContext?: LogContext
): void {
  logger.error(
    `API Error: ${endpoint}`,
    error,
    {
      endpoint,
      ...additionalContext
    }
  );
}

// Helper function for logging user actions
export function logUserAction(
  action: string,
  context?: LogContext
): void {
  logger.info(`User Action: ${action}`, context);
}
